// Centralized SEO configuration for FitAura AI.
// Edit this file to update meta tags, structured data, and per-route copy.

export const SITE = {
  url: "https://fitauraai.com",
  name: "FitAura AI",
  shortName: "FitAura",
  tagline: "Virtual Try-On, Powered by AI",
  defaultTitle: "FitAura AI – Virtual Try-On & AI Fitting Room",
  defaultDescription:
    "Upload your photo, paste any clothing link, and see how outfits look on you in seconds with FitAura AI's free virtual try-on.",
  defaultKeywords:
    "virtual try on, AI clothes try on, virtual fitting room, AI outfit generator, try clothes on photo, online fitting room India, AI fashion app, see clothes on yourself, virtual dressing room, AI stylist",
  defaultOgImage: "https://fitauraai.com/og-cover.png?v=20260703-exact",
  twitterHandle: "@fitauraai",
  locale: "en_US",
  alternateLocale: "en_IN",
  geo: {
    region: "IN-KA",
    placename: "Bangalore",
    position: "12.9716;77.5946",
    icbm: "12.9716, 77.5946",
  },
  social: {
    instagram: "https://instagram.com/fitauraai",
    twitter: "https://twitter.com/fitauraai",
    linkedin: "https://www.linkedin.com/company/fitauraai",
  },
  email: "support@fitauraai.com",
} as const;

export interface RouteSEO {
  title: string;
  description: string;
  keywords?: string;
  noindex?: boolean;
}

export const ROUTE_SEO: Record<string, RouteSEO> = {
  "/": {
    title: "FitAura AI – Free AI Virtual Try-On & Fitting Room",
    description:
      "Upload your photo, paste any clothing link, and see how outfits look on you in seconds with FitAura AI's free virtual try-on.",
    keywords:
      "virtual try on, AI try on clothes, virtual fitting room, AI outfit generator, see clothes on yourself, virtual dressing room India, AI fashion app",
  },
  "/about": {
    title: "About FitAura AI – Our Mission to Democratize Virtual Try-On",
    description:
      "Learn how FitAura AI uses generative AI to give shoppers an instant, photorealistic virtual try-on for any outfit, from any store, on any body.",
    keywords: "about FitAura, AI fashion startup India, virtual try on company",
  },
  "/privacy": {
    title: "Privacy Policy – FitAura AI",
    description:
      "Read how FitAura AI collects, stores, and protects your photos and personal data. Your images are private and never shared without consent.",
  },
  "/terms": {
    title: "Terms of Service – FitAura AI",
    description: "Terms governing use of the FitAura AI virtual try-on service.",
  },
  "/refund": {
    title: "Refund Policy – FitAura AI Credits",
    description: "How refunds work for FitAura AI credit packs and subscriptions.",
  },
  "/changelog": {
    title: "FitAura AI Changelog — Latest Updates & Release Notes",
    description:
      "Official FitAura AI release notes — new features, Android updates, and fixes for our AI virtual try-on app.",
    keywords:
      "FitAura AI changelog, FitAura AI release notes, FitAura AI updates, AI virtual try-on update, FitAura Android update",
  },
  "/auth": {
    title: "Sign In or Sign Up – FitAura AI",
    description:
      "Create your free FitAura AI account to start trying on outfits virtually with AI. Sign up in seconds with email or Google.",
  },
  "/checkout/success": {
    title: "Payment Successful – FitAura AI",
    description: "Your FitAura AI credits have been added to your account.",
    noindex: true,
  },
  "/onboarding": {
    title: "Welcome to FitAura AI – Quick Setup",
    description: "Tell us about your style preferences to get personalized outfit recommendations.",
    noindex: true,
  },
  "/app": {
    title: "Try On Clothes Virtually – FitAura AI",
    description:
      "Paste any product link or upload an outfit photo and see how it looks on you with AI-powered virtual try-on.",
    keywords: "virtual try on, AI try on clothes, paste product link try on",
  },
  "/app/style": {
    title: "AI Outfit Stylist – Build Looks with FitAura AI",
    description:
      "Generate complete styled outfits on your photo using AI. Pick a vibe, occasion, or style and let FitAura design the look.",
    keywords: "AI outfit stylist, AI outfit generator, virtual stylist",
  },
  "/app/aura": {
    title: "AuraScore – AI Fashion & Style Score | FitAura AI",
    description:
      "Get an honest AI-powered style score for any outfit. Color harmony, fit, silhouette, and personalized improvement tips.",
    keywords: "AI fashion score, outfit rating AI, style analyzer",
  },
  "/app/history": {
    title: "Your Try-On History – FitAura AI",
    description: "All your past virtual try-ons in one place.",
    noindex: true,
  },
  "/app/profile": {
    title: "Your Profile – FitAura AI",
    description: "Manage your FitAura AI profile, style preferences, and credits.",
    noindex: true,
  },
  "/app/photo-tips": {
    title: "Photo Tips for the Best AI Try-On Results | FitAura AI",
    description:
      "Learn how to take the perfect photo for FitAura AI: lighting, pose, framing, and background tips for photorealistic virtual try-on.",
    keywords: "best photo for virtual try on, AI try on photo tips",
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// JSON-LD builders
// ─────────────────────────────────────────────────────────────────────────────

export const organizationLd = {
  "@context": "https://schema.org",
  "@type": "Organization",
  name: SITE.name,
  url: SITE.url,
  logo: `${SITE.url}/app-icon-512.png`,
  description: SITE.defaultDescription,
  email: SITE.email,
  foundingLocation: {
    "@type": "Place",
    address: { "@type": "PostalAddress", addressCountry: "IN", addressRegion: "Karnataka" },
  },
  sameAs: [SITE.social.instagram, SITE.social.twitter, SITE.social.linkedin],
  contactPoint: {
    "@type": "ContactPoint",
    contactType: "customer support",
    email: SITE.email,
    availableLanguage: ["English", "Hindi"],
  },
};

export const websiteLd = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  name: SITE.name,
  url: SITE.url,
  potentialAction: {
    "@type": "SearchAction",
    target: `${SITE.url}/?q={search_term_string}`,
    "query-input": "required name=search_term_string",
  },
};

export const softwareAppLd = {
  "@context": "https://schema.org",
  "@type": "SoftwareApplication",
  name: SITE.name,
  applicationCategory: "LifestyleApplication",
  operatingSystem: "Web, iOS, Android",
  description: SITE.defaultDescription,
  url: SITE.url,
  image: SITE.defaultOgImage,
  screenshot: SITE.defaultOgImage,
  offers: {
    "@type": "Offer",
    price: "0",
    priceCurrency: "USD",
    description: "Free with paid credit packs available",
  },
  aggregateRating: {
    "@type": "AggregateRating",
    ratingValue: "4.8",
    ratingCount: "1240",
    bestRating: "5",
    worstRating: "1",
  },
  featureList: [
    "AI virtual try-on from any product link",
    "AI outfit stylist and generator",
    "AuraScore style and fit analysis",
    "Works with Amazon, Myntra, Zara, H&M and any image",
    "Photorealistic results in seconds",
  ],
};

export const faqLd = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: [
    {
      "@type": "Question",
      name: "How does FitAura AI virtual try-on work?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Upload a clear full-body photo of yourself, then paste a product link from any store (or upload an outfit image). Our AI generates a photorealistic image of you wearing the outfit in about 15 seconds.",
      },
    },
    {
      "@type": "Question",
      name: "Is FitAura AI free?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Install the FitAuraAI Android app from the Google Play Store to get a free welcome try-on credit and daily streak rewards. Affordable credit packs are available if you need more generations.",
      },
    },
    {
      "@type": "Question",
      name: "Which stores does FitAura support?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "FitAura works with most online clothing stores including Amazon, Myntra, Flipkart, Zara, H&M, ASOS, Shein, Nykaa Fashion, Ajio, and many more. You can also upload any outfit image directly.",
      },
    },
    {
      "@type": "Question",
      name: "Does FitAura work for Indian outfits like sarees and kurtas?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Yes. FitAura is built for global and Indian fashion and works well with sarees, kurtas, lehengas, sherwanis, and ethnic wear in addition to western outfits.",
      },
    },
    {
      "@type": "Question",
      name: "Are my photos kept private?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Your photos are stored privately in your account and are never shared, sold, or used to train models. You can delete them at any time from your history.",
      },
    },
    {
      "@type": "Question",
      name: "How accurate is the AI try-on?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "FitAura uses state-of-the-art generative AI to render photorealistic results. Accuracy improves with a clear, well-lit, full-body photo. See our Photo Tips guide for the best results.",
      },
    },
    {
      "@type": "Question",
      name: "Can I try on multiple outfits at once?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Yes. Use the AI Outfit Stylist to generate complete styled looks, or run multiple individual try-ons from your history.",
      },
    },
    {
      "@type": "Question",
      name: "Does FitAura work on mobile?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Absolutely. FitAura is fully responsive and works on iPhone, Android, tablets, and desktop browsers. Native iOS and Android apps are also available.",
      },
    },
  ],
};

export const howToLd = {
  "@context": "https://schema.org",
  "@type": "HowTo",
  name: "How to try on clothes virtually with FitAura AI",
  description: "Three simple steps to see any outfit on yourself using AI.",
  totalTime: "PT30S",
  step: [
    {
      "@type": "HowToStep",
      position: 1,
      name: "Upload your photo",
      text: "Upload a clear, full-body photo of yourself in good lighting.",
    },
    {
      "@type": "HowToStep",
      position: 2,
      name: "Paste a product link or outfit image",
      text: "Paste any clothing product URL from your favorite store, or upload an outfit image directly.",
    },
    {
      "@type": "HowToStep",
      position: 3,
      name: "See the AI try-on result",
      text: "FitAura AI generates a photorealistic image of you wearing the outfit in about 15 seconds. Save, share, or refine it.",
    },
  ],
};

export const breadcrumbLd = (items: Array<{ name: string; url: string }>) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: items.map((item, idx) => ({
    "@type": "ListItem",
    position: idx + 1,
    name: item.name,
    item: item.url,
  })),
});

export function buildCanonical(pathname: string): string {
  const clean = pathname.replace(/\/+$/, "") || "/";
  return `${SITE.url}${clean === "/" ? "" : clean}`;
}
